import { useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowRightLeft, Ruler } from "lucide-react";

const CATEGORIES = {
  Length: {
    base: "m",
    units: {
      mm: 0.001, cm: 0.01, m: 1, km: 1000,
      in: 0.0254, ft: 0.3048, yd: 0.9144, mi: 1609.344,
    },
  },
  Weight: {
    base: "kg",
    units: { mg: 0.000001, g: 0.001, kg: 1, t: 1000, oz: 0.0283495, lb: 0.453592 },
  },
  Volume: {
    base: "l",
    units: { ml: 0.001, l: 1, "fl oz": 0.0295735, cup: 0.24, pint: 0.473176, gal: 3.78541 },
  },
  Temperature: {
    base: "C",
    units: { C: "C", F: "F", K: "K" }, // special
  },
};

function convertTemp(v, from, to) {
  let c;
  if (from === "C") c = v;
  else if (from === "F") c = (v - 32) * (5 / 9);
  else c = v - 273.15;
  if (to === "C") return c;
  if (to === "F") return c * (9 / 5) + 32;
  return c + 273.15;
}

function convert(value, from, to, category) {
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  if (category === "Temperature") return convertTemp(n, from, to);
  const cat = CATEGORIES[category];
  const meters = n * cat.units[from];
  return meters / cat.units[to];
}

export default function UnitConverterPage() {
  const [category, setCategory] = useState("Length");
  const [from, setFrom] = useState("m");
  const [to, setTo] = useState("ft");
  const [value, setValue] = useState("1");

  const units = Object.keys(CATEGORIES[category].units);
  const result = useMemo(() => convert(value, from, to, category), [value, from, to, category]);

  function swap() {
    setFrom(to);
    setTo(from);
  }

  function onCategoryChange(c) {
    setCategory(c);
    const u = Object.keys(CATEGORIES[c].units);
    setFrom(u[0]);
    setTo(u[1] || u[0]);
  }

  return (
    <div className="animate-fade-up max-w-3xl">
      <div className="mb-8">
        <span className="label-caps">Tool</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Unit Converter.</h1>
        <p className="mt-2 text-[color:var(--text-muted)]">Length, weight, volume, temperature. Local and instant.</p>
      </div>

      <div className="card-tactile rounded-2xl p-6 space-y-4">
        <div>
          <label className="label-caps mb-2 block">Category</label>
          <Select value={category} onValueChange={onCategoryChange}>
            <SelectTrigger data-testid="uc-category" className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.keys(CATEGORIES).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
          <div>
            <label className="label-caps mb-2 block">From</label>
            <div className="flex gap-2">
              <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} data-testid="uc-value" className="flex-1" />
              <Select value={from} onValueChange={setFrom}>
                <SelectTrigger data-testid="uc-from" className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>{units.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <button onClick={swap} data-testid="uc-swap" className="h-11 w-11 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface)] flex items-center justify-center hover:bg-[color:var(--surface-2)] self-end md:mb-0 mb-0" aria-label="Swap units">
            <ArrowRightLeft className="h-4 w-4 text-[color:var(--primary-ink)]" />
          </button>
          <div>
            <label className="label-caps mb-2 block">To</label>
            <div className="flex gap-2">
              <div data-testid="uc-result" className="flex-1 h-11 px-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] flex items-center font-semibold">
                {typeof result === "number" ? result.toLocaleString(undefined, { maximumFractionDigits: 6 }) : "—"}
              </div>
              <Select value={to} onValueChange={setTo}>
                <SelectTrigger data-testid="uc-to" className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>{units.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="text-xs text-[color:var(--text-muted)] flex items-center gap-1 pt-2"><Ruler className="h-3 w-3" /> Works offline once installed.</div>
      </div>
    </div>
  );
}
